import { Box, IconButton, Stack, Tooltip, styled } from '@mui/material';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { FileDownloadOutlined } from '@mui/icons-material';
import { ImageRatio } from '@synergy/models';
import { LockIconNew, PhotoIcon } from '@synergy/theme';
import { MouseEvent, PropsWithChildren } from 'react';
import { getHeightByRatio, shouldForwardPropExclude } from '@synergy/utils';
import { useComponentSize } from '@synergy/components-next';

const DropzoneBox = styled(Stack, { shouldForwardProp: shouldForwardPropExclude('height', 'error') })<{
    height: number;
    error: boolean;
}>(({ theme, height, error }) => ({
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    height,
    background: 'none',
    position: 'relative',
    borderRadius: 4,
    border: '1.5px solid',
    borderColor: error ? theme.palette.error.main : theme.palette.grey[400],
    '&:hover': {
        borderColor: !error ? theme.palette.common.black : undefined,
    },
    '&:active': {
        border: !error ? `2px solid ${theme.palette.primary.main}` : undefined,
    },
}));

const ImageBox = styled(Box, { shouldForwardProp: shouldForwardPropExclude('path') })<{ path?: string }>(
    ({ theme, path }) => ({
        width: '100%',
        height: '100%',
        display: 'inline-block',
        position: 'relative',
        borderRadius: 4,
        '&:after': {
            position: 'absolute',
            content: '""',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            display: 'inline-block',
            borderRadius: 2,
            background: path
                ? 'linear-gradient(0deg, rgba(33, 39, 46, 0.75), rgba(33, 39, 46, 0.75))'
                : theme.palette.common.white,
        },
    })
);

const Image = styled('img')(() => ({
    height: '100%',
    width: '100%',
    display: 'block',
    borderRadius: 4,
}));

const DropzoneIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
        backgroundColor: '#0C7CD5',
    },
    mb: 1,
    p: 1.25,
    '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[400],
    },
}));

type PictureDropzoneProps = PropsWithChildren<DropzoneOptions> & {
    error: boolean;
    imageRatio: ImageRatio;
    path?: string;
    alt?: string;
    disabled?: boolean;
};

// HINT: accept with MIME type is not working for Chrome with Ubuntu as of Feb. 2023
// if this is fixed, the accept prop can be used instead of custom validation in the onDrop function
/**
 * Saves the picture shown. A banner could be uploaded but not taken back out
 * of the system, so whoever needed the file again asked for it around
 * (key-user brief A3.4).
 */
const downloadPicture = async (path: string, alt?: string): Promise<void> => {
    const name = path.startsWith('data:')
        ? alt || 'banner'
        : decodeURIComponent(path.split('?')[0].split('/').pop() || alt || 'banner');
    try {
        const response = await fetch(path);
        if (!response.ok) {
            throw new Error(String(response.status));
        }
        const url = URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        link.click();
        URL.revokeObjectURL(url);
    } catch {
        // The media host may not answer a cross-origin fetch; the picture then
        // opens on its own, where the browser saves it.
        window.open(path, '_blank', 'noopener');
    }
};

export const PictureDropzone = (props: PictureDropzoneProps) => {
    const { error, imageRatio, path, alt, disabled, children, ...dropzoneProps } = props;
    const { getRootProps, getInputProps } = useDropzone({
        ...dropzoneProps,
        noDrag: disabled,
    });
    const { measuredRef, width } = useComponentSize();

    return (
        <DropzoneBox
            {...getRootProps({
                onClick: e => disabled && e.stopPropagation(),
            })}
            height={getHeightByRatio(imageRatio, width)}
            error={error}
        >
            <Box
                sx={{
                    height: '100%',
                    position: 'absolute',
                    width: '100%',
                    background: 'none',
                }}
            >
                <ImageBox path={path}>
                    <Image ref={measuredRef} src={path} alt={alt} />
                </ImageBox>
            </Box>
            <input {...getInputProps()} />
            {path && (
                <Tooltip title="Bild herunterladen">
                    <IconButton
                        aria-label="Bild herunterladen"
                        size="small"
                        onClick={(event: MouseEvent) => {
                            // The whole box opens the file picker on click.
                            event.stopPropagation();
                            void downloadPicture(path, alt);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            color: 'common.white',
                            backgroundColor: 'rgba(33, 39, 46, 0.6)',
                            '&:hover': { backgroundColor: 'rgba(33, 39, 46, 0.85)' },
                        }}
                    >
                        <FileDownloadOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <DropzoneIconButton disabled={disabled}>{disabled ? <LockIconNew /> : <PhotoIcon />}</DropzoneIconButton>
            <Stack sx={{ mt: 0.5, zIndex: 1, alignItems: 'center' }}>{children}</Stack>
        </DropzoneBox>
    );
};
